/*****************************************************************************
 * This component is the User Permissions that can be set by the administrator.
 * It is used in conjunction with create.js or edit.js
  
 *
 * Created by: Robert Gautier/Joe J
 * Date: 3/2019
 *****************************************************************************/

import React, { Component } from "react";
import { connect } from "react-redux";
import { Form } from "react-form";
import { withRouter } from "react-router-dom";

import Paper from "@material-ui/core/Paper";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import Table from "@material-ui/core/Table";
import { withStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";

import Checkbox from "../common/Checkbox";
import WarningDialog from "../common/WarningDialog";
import EnhancedTableHead from "../common/EnhancedTableHead";
import CustomTableCell from "../common/TableCell";
import { createSelector } from "../common/orm";
import { Permission } from "../page/models";

import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import HelpLabel from "../common/HelpLabel";
import Box from "@material-ui/core/Box";

import { User } from "./models";


// Get all "Page" objects and order by name
// // Also include a mapping to _sections
const allPages = createSelector((schema) => {
    return schema.Page.all().orderBy("name").toModelArray();
});

const getUser = User.selectByUrlId();

const userPermissions = createSelector(
    (state, ownProps) => parseInt(ownProps.match.params["id"]),
    (session, objId) => {
        return session.Permission.filter((perm) => perm.user === objId).toRefArray();
    }
);

// CSS in JS styles
const styles = (theme) => ({
    italics: {
        fontStyle: "italic",
    },
    table: {
        minWidth: 500,
    },
    flex: {
        flex: 1,
    },
    button: {
        float: "right",
    },
    gutterBottom: {
        marginBottom: ".6em",
    },
    root: {
        width: "100%",
        overflowX: "auto",
    },
    deleteWidth: {
        minWidth: 34,
        width: 34,
        margin: "0 8px 0 0",
    },
    centerAlign: {
        textAlign: "center",
    },
    nowrap: {
        whiteSpace: "nowrap",
    },
});

// The table columns on the "edit" permissions page.
// http://localhost:3000/#/adm/edit/ (id)
const columnData = [
    { id: "name", numeric: false, label: "Module Name", allowSort: true, width: "40%" },
    { id: "access", numeric: false, label: "Access", allowSort: false, width: "auto" },
    { id: "dashboard", numeric: false, label: "Default Dashboard", allowSort: false, width: "auto" },
];

// The root component of the page
// http://localhost:3000/#/adm/edit/ (id)
class UserPermissions extends Component {
    constructor(props, context) {
        super(props, context);
        // Default states
        this.state = {
            order: "asc",
            orderBy: "name",
            setDefault: true,
            dialogOpen: false,
        };
    }


    ACCESS_KEYS = [
        "fullaccess",
        "fullaccess_no_parent_delete",
        "fullaccess_no_delete",
        "readonly",
        "readonlyexternal",
        "noaccess",
    ];

    getAccessValueFromPerm = (perm) => {
        if (!perm) return "";
        for (const key of this.ACCESS_KEYS) {
            if (perm[key]) return key;
        }
        return "";
    };

    buildAccessPayload = (selectedKey) => ({
        fullaccess: false,
        fullaccess_no_parent_delete: false,
        fullaccess_no_delete: false,
        readonly: false,
        noaccess: false,
        readonlyexternal: false,
        [selectedKey]: true,
    });

    // When a table column is sorted
    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = "desc";

        if (this.state.orderBy === property && this.state.order === "desc") {
            order = "asc";
        }

        order === "desc"
            ? this.props.pages.sort((a, b) => (b[orderBy].toUpperCase() < a[orderBy].toUpperCase() ? -1 : 1))
            : this.props.pages.sort((a, b) => (a[orderBy].toUpperCase() < b[orderBy].toUpperCase() ? -1 : 1));

        this.setState({ order, orderBy });
    };


    accessChanged = (selectedKey, page_id, user_id, perm_exists) => {
        const { ormPermissionCreate, ormPermissionUpdate } = this.props;
        const payload = this.buildAccessPayload(selectedKey);
        if (!perm_exists) {
            ormPermissionCreate({ page: page_id, user: user_id, ...payload });
        } else {
            ormPermissionUpdate({ id: perm_exists.id, ...payload });
        }
    };

    //update the checkbox,
    checkboxUpdated = (value, page_id, user_id, perm_exists, typeOfAccess) => {
        const { ormPermissionCreate, ormPermissionUpdate, ormUserUpdate, ormUserUpdateLocalOnly } = this.props;

        const buildAccessPayload = (selectedKey, selectedValue) => {
            const base = this.buildAccessPayload("noaccess"); // start with all false then set below
            Object.keys(base).forEach((k) => (base[k] = false));
            return { ...base, [selectedKey]: selectedValue };
        };

        if (typeOfAccess === "dashboard") {
            //permissions have not been set and are undefined
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    dashboard: value, //readonly will be set to true
                });
                //update permissions that have already been set
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    dashboard: value,
                });
            }
            //update user model so we can route directly from login screen
            if (value) {
                ormUserUpdate({
                    id: user_id,
                    default_dashboard: page_id,
                });
                ormUserUpdateLocalOnly({
                    id: user_id,
                    default_dashboard: page_id,
                });
            } else {
                ormUserUpdate({
                    id: user_id,
                    default_dashboard: null,
                });
            }
        } else if (typeOfAccess === "readonly") {
            //permissions have not been set and are undefined
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("readonly", value),
                });
                //update permissions that have already been set
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("readonly", value),
                });
            }
        } else if (typeOfAccess === "fullaccess") {
            //permissions have not been set and are undefined
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("fullaccess", value),
                });
            } else {
                //update permissions that have already been set
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("fullaccess", value),
                });
            }
        } else if (typeOfAccess === "fullaccess_no_parent_delete") {
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("fullaccess_no_parent_delete", value),
                });
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("fullaccess_no_parent_delete", value),
                });
            }
        } else if (typeOfAccess === "fullaccess_no_delete") {
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("fullaccess_no_delete", value),
                });
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("fullaccess_no_delete", value),
                });
            }
        } else if (typeOfAccess === "readonlyexternal") {
            //permissions have not been set and are undefined
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("readonlyexternal", value),
                });
                //update permissions that have already been set
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("readonlyexternal", value),
                });
            }
        } else {
            //this is for NO ACCESS SITUATION
            //permissions have not been set and are undefined
            if (perm_exists === undefined) {
                ormPermissionCreate({
                    page: page_id,
                    user: user_id,
                    ...buildAccessPayload("noaccess", value),
                });
                //update permissions that have already been set
            } else {
                ormPermissionUpdate({
                    id: perm_exists.id,
                    ...buildAccessPayload("noaccess", value),
                });
            }
        }
    };

    setDefaultPermission = () => {
        const { permissions, useInfo, ormPermissionCreate, pages } = this.props;
        this.setState({ setDefault: false });
    
        // Set permissions to NO ACCESS for creation of a new user (secure default)
        if (permissions.length < 1) {
            pages.map((p) => {
                return ormPermissionCreate({
                    page: p.id,
                    user: useInfo.id,
                    noaccess: true,
                    fullaccess: false,
                    fullaccess_no_parent_delete: false,
                    fullaccess_no_delete: false,
                    readonly: false,
                    readonlyexternal: false,
                });
            });
        }
    };

    render() {
        const { classes, pages, useInfoCreate, permissions } = this.props;
        var { useInfo } = this.props;
        const { order, orderBy, setDefault, dialogOpen } = this.state;
        if (!useInfo || useInfo.name === "Not Found") useInfo = useInfoCreate;

        if (setDefault) {
            //set permission to full access for new user
            this.setDefaultPermission();
        }

        return (
            <Form getApi={(el) => (this.formApi = el)} dontValidateOnMount="true" validateOnSubmit="true">
                {(formApi) => (
                    <form>
                        <>
                            <Box
                                display="flex"
                                alignItems="center"
                            >
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom={true}
                                    style={{ marginLeft: 3 }}
                                >
                                    Note: each module needs to have an access filter. For new users, modules default to "No Access" until an admin grants access.
                                </Typography>
                                <Box
                                    sx={{
                                        paddingTop: 5,
                                        marginLeft: 5,
                                    }}
                                >
                                    <HelpLabel
                                        inputLabel=""
                                        helpText={`
                                        <strong>-- Select Access --</strong>: This is a default option that indicates no access has been set. It is equivalent to 'Full Access (No primary or related record delete)'.

                                        <strong>Full Access (Can delete anything)</strong>: Gives the user full access to the module including deleting primary records and any related records.

                                        <strong>Full Access (No primary record delete)</strong>: Gives the user full access to the module including deleting related records, but NOT primary records.

                                        <strong>Full Access (No primary or related record delete)</strong>: Gives the user full access to the module except deleting any records.

                                        <strong>Read Only</strong>: Gives the user read-only access to view all records in the module. Users cannot create, edit, or delete any records. Saved searches set to "All", "All Including External", "Creator" (their own), or "Admin" are visible to this user.

                                        <strong>Read Only (External)</strong>: Gives external users (outside the organization) read-only access to records and searches explicitly shared with external users. Users cannot create, edit, or delete records. Only saved searches explicitly set to "All Including External Read Only Users" are visible; searches set to "All", "Creator", or "Admin" are hidden from this user.
                                        
                                        <strong>No Access</strong>: User has no access to the module.

                                        <strong>Default Dashboard</strong>: Sets the module as the default dashboard for the user. Only modules with 'Full Access' or 'Read Only' can be set as the default dashboard.
                                        `}
                                    />
                                </Box>
                            </Box>
                            <Paper className={classes.root}>
                                <Table className={classes.table}>
                                    <EnhancedTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={this.handleRequestSort}
                                        columnData={columnData}
                                    />

                                    {/* this is the layout of the table for the pages & checkboxes map function which will loop based on total amount of pages*/}
                                    <TableBody>
                                        {/* loop through the pages from DB one-by-one where "n is the page",
                                    PERMISSIONS are all the permissions in DB. permission.FIND looks and finds the "first" element in the array
                                    that matches "permissions.pageID" with "pages.id" when that is found it is placed in perm (i.e. permissions)
                                    to be looped through the return statement which will populate the checkboxes, etc. 
                                    */}
                                        {pages.map((page) => {
                                            const perm = permissions.find(function (permission) {
                                                return permission.page === page.id;
                                            });
                                            return (
                                                <TableRow hover key={page.id}>
                                                    {/* page name */}
                                                    <CustomTableCell>{page.name}</CustomTableCell>
                                                    {/* Checkboxes for each page start with fullaccess, readonly and then noaccess. */}
                                                    <CustomTableCell>
                                                        <FormControl fullWidth>
                                                            <Select
                                                                value={this.getAccessValueFromPerm(perm)}
                                                                onChange={(e) => this.accessChanged(e.target.value, page.id, useInfo.id, perm)}
                                                                displayEmpty
                                                                inputProps={{ "aria-label": "Access" }}
                                                            >
                                                                <MenuItem value={""}>-- Select Access --</MenuItem>
                                                                <MenuItem value={"fullaccess"}>Full Access (Can delete anything)</MenuItem>
                                                                <MenuItem value={"fullaccess_no_parent_delete"}>Full Access (No primary record delete)</MenuItem>
                                                                <MenuItem value={"fullaccess_no_delete"}>Full Access (No primary or related record delete)</MenuItem>
                                                                <MenuItem value={"readonly"}>Read Only</MenuItem>
                                                                <MenuItem value={"readonlyexternal"}>Read Only (External)</MenuItem>
                                                                <MenuItem value={"noaccess"}>No Access</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </CustomTableCell>
                                                    <CustomTableCell>
                                                        <Checkbox
                                                            checked={page.id === parseInt(useInfo.default_dashboard)}
                                                            disabled={false}
                                                            label=""
                                                            field="dashboard"
                                                            className={classes.deleteWidth}
                                                            eventHandle={(value) => {
                                                                const access = this.getAccessValueFromPerm(perm);
                                                                const eligible = ["fullaccess", "fullaccess_no_parent_delete", "fullaccess_no_delete", "readonly", "readonlyexternal"];
                                                                if (value && (!perm || !eligible.includes(access))) {
                                                                    this.setState({ dialogOpen: true });
                                                                } else {
                                                                    this.checkboxUpdated(value, page.id, useInfo.id, perm, "dashboard");
                                                                }

                                                            }}
                                                        />
                                                    </CustomTableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {pages.length < 1 && (
                                            <TableRow>
                                                <CustomTableCell colSpan={3} className={classes.centerAlign}>
                                                    No Permissions Defined
                                                </CustomTableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>

                            
                            <WarningDialog
                                confirmAction={() => {
                                    this.setState({ dialogOpen: false });
                                }}
                                cancelAction={() => this.setState({ dialogOpen: false })}
                                open={dialogOpen}
                                confirmText="Ok"
                                noDeleteButtons
                                title="Module doesnt have Access"
                                text="Only Modules that are set as 'Full Access' or 'Read Only' can be set as the default Dashboard"
                            />
                        </>
                    </form>
                )}
            </Form>
        );
    }
}
UserPermissions.displayName = "UserPermissions";
// This connects the redux-orm "allPages" function and allows the UserPermissions to have the props "pages"
// The second part loads all orm actions into the UserPermissions props for both Page and Permission: ormPageCreate, ormPermissionCreate, orPageUpdate, etc.
UserPermissions = connect(
    (state, ownProps) => ({
        pages: allPages(state),
        useInfo: getUser(state, ownProps), //state prop "useInfo" to use with the page. Note ownProps are declared as they are needed for the orm function, however are not used
        permissions: userPermissions(state, ownProps),
        authState: state.auth,
    }),
    {
        // ...Page.actions, //orm actionCreators to DB for Page ex. ormPageUpdate(values), ormPageCreate(values)
        ...Permission.actions,
        ...User.actions, //orm actionCreators to DB for Permissions ex. ormPermissionUpdate(values), ormPermissionCreate(values)
    }
)(UserPermissions);

export default withStyles(styles)(withRouter(UserPermissions));